import { useEffect, useRef } from 'react';
import { Phone, PhoneOff, Video, Bot, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface IncomingCallNotificationProps {
  callerName: string;
  callerRole?: 'specialist' | 'caregiver';
  onAccept: () => void;
  onDecline: () => void;
  isBotCall?: boolean;
}

export function IncomingCallNotification({
  callerName,
  callerRole = 'specialist',
  onAccept,
  onDecline,
  isBotCall = false,
}: IncomingCallNotificationProps) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const ringCountRef = useRef(0);
  const MAX_RINGS = 15;

  // Play ringtone sound effect using Web Audio API (max 15 rings)
  useEffect(() => {
    ringCountRef.current = 0;
    
    const playRingtone = () => {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const playRingPattern = () => {
          if (!audioContextRef.current || audioContextRef.current.state === 'closed') return;
          
          // Stop after MAX_RINGS
          ringCountRef.current += 1;
          if (ringCountRef.current > MAX_RINGS) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Auto-decline after max rings (no answer)
            onDecline();
            return;
          }
          
          const now = audioContextRef.current.currentTime;
          
          // Ring pattern: two quick ascending tones
          const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5
          
          frequencies.forEach((freq, i) => {
            const oscillator = audioContextRef.current!.createOscillator();
            const gainNode = audioContextRef.current!.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContextRef.current!.destination);
            
            oscillator.frequency.value = freq;
            oscillator.type = 'sine';
            
            const startTime = now + (i * 0.15);
            gainNode.gain.setValueAtTime(0, startTime);
            gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0, startTime + 0.2);
            
            oscillator.start(startTime);
            oscillator.stop(startTime + 0.25);
          });
        };

        // Play immediately and then every 2 seconds
        playRingPattern();
        intervalRef.current = setInterval(playRingPattern, 2000);

        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
          }
        };
      } catch (e) {
        console.log('Audio not supported:', e);
        return () => {};
      }
    };

    const cleanup = playRingtone();
    
    return () => {
      cleanup?.();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [onDecline]);

  const roleLabel = isBotCall ? 'AI Assistant' : (callerRole === 'specialist' ? 'Sleep Coach' : 'Caregiver');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <Card className={`w-full max-w-sm p-6 mx-4 animate-pulse-slow ${isBotCall ? 'border-purple-500/50 bg-gradient-to-br from-purple-500/10 to-background' : ''}`}>
        <div className="text-center space-y-4">
          {/* Call icon with animation */}
          <div className="relative mx-auto w-20 h-20">
            <div className={`absolute inset-0 ${isBotCall ? 'bg-purple-500/20' : 'bg-primary/20'} rounded-full animate-ping`} />
            <div className={`relative w-full h-full ${isBotCall ? 'bg-gradient-to-br from-purple-600 to-purple-800' : 'bg-primary'} rounded-full flex items-center justify-center`}>
              {isBotCall ? (
                <Bot className="h-10 w-10 text-white" />
              ) : (
                <Video className="h-10 w-10 text-primary-foreground" />
              )}
            </div>
          </div>

          {/* Caller info */}
          <div>
            <h2 className="text-xl font-semibold flex items-center justify-center gap-2">
              {isBotCall && <Sparkles className="h-5 w-5 text-purple-500" />}
              {isBotCall ? 'AI Sleep Coach Calling' : 'Incoming Video Call'}
            </h2>
            <p className="text-muted-foreground mt-1">{callerName}</p>
            <p className="text-sm text-muted-foreground/70">{roleLabel}</p>
            {isBotCall && (
              <p className="text-xs text-purple-500 mt-2">Your scheduled check-in is ready</p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex justify-center gap-4 pt-4">
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full w-14 h-14"
              onClick={onDecline}
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
            <Button
              size="lg"
              className="rounded-full w-14 h-14 bg-green-600 hover:bg-green-700"
              onClick={onAccept}
            >
              <Phone className="h-6 w-6" />
            </Button>
          </div>

          {/* Labels */}
          <div className="flex justify-center gap-12 text-sm text-muted-foreground">
            <span>Decline</span>
            <span>Accept</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
